import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgOtpInputModule } from 'ng-otp-input';
import { VerifyIdentityComponent } from './verify-identity/verify-identity.component';
import {
  ComponentBase,
  FORGET_PASSWORD_INPUT,
  VERFICATION_RESET_INPUT,
  VERIFY_SIGNUP_INPUT,
  VerifyMobileNumberType,
  SharedService,
  AutoRegistrationService,
  BusinessDetailsService,
} from 'src/app/core';
import { Config as NgOtpInputConfig } from 'ng-otp-input/lib/models/config';
import { ToastrService } from 'ngx-toastr';
import { Observable, interval, of } from 'rxjs';
import { AutoRegistrationConfig } from '../../models/auto-registration-config';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { SalesChatService } from 'src/app/core';
import { CONFIG } from '../../registration-config';

@Component({
  selector: 'app-verification',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgOtpInputModule, VerifyIdentityComponent],
  templateUrl: './verification.component.html',
  styleUrls: ['./verification.component.scss'],
})
export class VerificationComponent extends ComponentBase implements OnInit, OnChanges {
  @Input() isVerifyRoute?: boolean = false;
  /** Registration cycle log ID from parent in inline mode */
  @Input() inlineRegistrationCycleLogId?: string;

  /** Phone number or user identifier from parent in inline mode */
  @Input() inlineUser?: string;

  /** Verify type (SMS or Whatsapp) from parent in inline mode */
  @Input() inlineVerifyType?: VerifyMobileNumberType;

  /** Emitted when verification succeeds to close the sidebar (inline mode) */
  @Output() closeSidebar = new EventEmitter<void>();
  
  /** Registered mobile number displayed to the user */
  public registeredMobileNumber: string = '';

  /** When true, user can request a new verification code (resend) */
  openResendCode: boolean = false;

  /** Payload for resend code (forget password / change verify method) */
  resendCodeData: FORGET_PASSWORD_INPUT = {
    UserName: undefined,
    VerifyMobileNumberType: undefined,
  };

  /** Payload for reset password verification */
  verifyResetData: VERFICATION_RESET_INPUT = {
    UserName: undefined,
    Code: undefined,
  };

  /** Payload for sign-up verification */
  verifySignUpData: VERIFY_SIGNUP_INPUT = {
    RegistrationCycleLogId: undefined,
    VerificationCode: undefined,
  };

  /** Configuration for the OTP input component (length = 4, numeric, auto-focus first field) */
  otpInputConfig: NgOtpInputConfig = {
    length: 4,
    allowNumbersOnly: true,
    disableAutoFocus: true,
  };

  /** Observable countdown value for resend code timer (initialized so template async pipe never sees undefined) */
  count$: Observable<number> = of(59);

  /** Controls OTP input visibility */
  showOtpInput: boolean = false;

  /** Reference to verify type enum (SMS or Whatsapp) */
  verifyMobileNumberType = VerifyMobileNumberType;

  /** Request flow type: 'auto' (sign-up) or 'normal' (forget password); derived from verifySignUpData */
  get requestType(): string {
    return this.verifySignUpData.RegistrationCycleLogId ? 'auto' : 'normal';
  }

  /** Current OTP code entered by the user */
  otpCode: string = '';

  /** When true, shows the verify identity (change method) component */
  showVerifyIdentity: boolean = false;

  /** When true, confirm button shows loading state */
  confirmLoading: boolean = false;

  /** When true, OTP inputs show red border (set when verify request fails) */
  otpError: boolean = false;

  /** Returns translated verification message with masked phone (last 2 digits) */
  get getVerificationMessage(): string {
    const isWhatsApp = +this.resendCodeData.VerifyMobileNumberType === 2;
    const messageType = isWhatsApp ? 'Enter_The_Code_WhatsApp' : 'Enter_The_Code_Phone';
    let maskedNumber = '***';
    if (this.registeredMobileNumber && this.registeredMobileNumber.length >= 2) {
      const lastTwoDigits = this.registeredMobileNumber.slice(-2);
      maskedNumber = `***${lastTwoDigits}`;
    }
    const baseMessage = this.translateService.instant(messageType);
    return baseMessage.replace('{{number}}', maskedNumber);
  }

  constructor(
    private salesChatService: SalesChatService,
    private readonly autoRegistrationService: AutoRegistrationService,
    private readonly sharedService: SharedService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    @Inject(CONFIG) public config: AutoRegistrationConfig,
    public readonly translateService: TranslateService,
    readonly businessDetailsService: BusinessDetailsService,
    readonly title: Title,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();

    translateService.onLangChange.subscribe((_) => {
      title.setTitle(translateService.instant('Title_verfication_phone'));
    });
    setTimeout(() => {
      title.setTitle(translateService.instant('Title_verfication_phone'));
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inlineVerifyType'] && this.inlineVerifyType) {
      this.resendCodeData = {
        ...this.resendCodeData,
        VerifyMobileNumberType: this.inlineVerifyType,
      };
    }
  }

  ngOnInit(): void {
    this.salesChatService.chatScript();
    if (this.inlineRegistrationCycleLogId) {
      this.verifySignUpData = {
        RegistrationCycleLogId: this.inlineRegistrationCycleLogId,
      };
      this.sharedService.setCookie('RegistrationCycleLogId', String(this.inlineRegistrationCycleLogId), 30);
    }

    if (this.inlineUser) {
      this.verifyResetData.UserName = this.inlineUser;
      this.resendCodeData.UserName = this.inlineUser;
      this.registeredMobileNumber = this.inlineUser;
    }

    this.resendCodeData = {
      ...this.resendCodeData,
      VerifyMobileNumberType:
        this.inlineVerifyType ?? this.verifyMobileNumberType.SMS,
    };

    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (regId) {
      this.businessDetailsService
        .getRegisteredPhases({
          RegistrationCycleLogId: +regId,
        })
        .pipe(
          map((response) => response?.Result?.One),
          takeUntil(this.destroy$)
        )
        .subscribe((response) => {
          if (response?.OneData?.MobileNumber) {
            this.registeredMobileNumber = response?.OneData?.MobileNumber;
          }
        });
    }

    setTimeout(() => {
      this.showOtpInput = true;
      const shouldAutoResend =
        this.sharedService.isMobileNumberVerified === false &&
        !!this.sharedService.userData &&
        !this.sharedService.basicDetails &&
        !this.sharedService.businessDetails;
      if (shouldAutoResend) this.onResendCode();
      this.countDown();
      this.cdr.markForCheck();
    }, 0);
  }

  /** Handles backToVerification event from verify-identity; resets OTP and resends with new verifyType */
  onBackToVerificationFromIdentity(payload?: { verifyType: any }): void {
    if (payload?.verifyType) {
      this.resendCodeData = {
        ...this.resendCodeData,
        VerifyMobileNumberType: payload.verifyType,
      };
      this.otpCode = '';
      this.openResendCode = false;
      this.onResendCode();

      this.cdr.markForCheck();
    }
  }

  /** Hides the verify identity (change method) component */
  onCloseVerifyIdentity(): void {
    this.showVerifyIdentity = false;
  }

  /** Called when user types in OTP input; stores the value and clears error state */
  onOtpInputChange(otpCode: string): void {
    this.otpCode = otpCode;
    this.otpError = false;
  }

  /** Called when user clicks confirm; triggers verification if OTP is complete */
  onConfirm(): void {
    if (this.otpCode && this.otpCode.length === 4 && !this.confirmLoading) {
      this.confirmLoading = true;
      this.onFill(this.otpCode);
    }
  }

  /** Submits the OTP code: sign-up verification (closeSidebar) or reset password (navigate) */
  onFill(otpCode: string): void {
    if (otpCode.length === 4) {
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) {
        this.confirmLoading = false;
        this.toastr.error(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
        return;
      } else {
        this.verifySignUpData = {
          ...this.verifySignUpData,
          RegistrationCycleLogId: regId,
          VerificationCode: otpCode,
        };

        this.autoRegistrationService
          .verificationPhaseOne(this.verifySignUpData)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.confirmLoading = false))
          )
          .subscribe((res) => {
            this.checkResponse = this.sharedService.checkResponseStatus(
              res.Message,
              res.State
            );
            this.otpError = !this.checkResponse;
            if (this.checkResponse) {
              this.sharedService.playSound('assets/sounds/done.mp3');
              this.closeSidebar.emit()
            };
          });
      }
    }
  }

  /** Starts 59-second countdown for resend code; sets openResendCode when done */
  private countDown(): void {
    const initialCount = 59;

    this.count$ = interval(1000).pipe(
      takeUntil(this.destroy$),
      take(initialCount + 1),
      map((count) => initialCount - count),
      tap((count) => {
        if (count === 0) {
          this.openResendCode = true;
        }
      })
    );
  }

  /** Resends verification code (resendCode for sign-up or forgetPassword for reset) */
  onResendCode(): void {
    this.openResendCode = false;
    if (this.requestType === 'auto') {
      const regId = this.sharedService.getCookie('RegistrationCycleLogId');
      if (!regId) {
        this.toastr.error(this.translateService.instant('Registration_Session_Expired'));
        this.router.navigate(['registration-cycle']);
        return;
      }
      if (this.resendCodeData.VerifyMobileNumberType) {
        this.autoRegistrationService
          .resendCode({
            VerifyMobileNumberType: this.resendCodeData.VerifyMobileNumberType,
            RegistrationCycleLogId: regId,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {

            if (res.State == 100) {
              this.countDown();
            }

            this.sharedService.checkResponseStatus(res.Message, res.State);

          });
      }
    }

    if (this.requestType === 'normal') {
      if (
        this.resendCodeData.UserName &&
        this.resendCodeData.VerifyMobileNumberType
      ) {
        this.autoRegistrationService
          .forgetPassword(this.resendCodeData)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {

            if (res.State == 100) {
              this.countDown();
            }

            this.sharedService.checkResponseStatus(res.Message, res.State);
          });
      } else {
        this.toastr.error('Error Occcurs , please try on another time !');
      }
    }
  }
}
